// ./src/components/features/authentication/ResetPasswordForm/ResetPasswordForm.tsx
import React, { useState } from "react";
import { Stack, Button, PasswordInput, Text, Alert } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconInfoCircle, IconCheck } from "@tabler/icons-react";

import { useAuth } from "@/hooks";

export interface ResetPasswordFormData {
  password: string;
}

export interface ResetPasswordFormErrors {
  password?: string;
  general?: string;
}

export interface ResetPasswordFormProps {
  /** The reset request ID from the email link */
  requestId: string;

  /** Callback function called when password reset is successful */
  onResetSuccess?: () => void;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  requestId,
  onResetSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<ResetPasswordFormErrors>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const { resetPassword } = useAuth({ autoCheck: false });

  const form = useForm<ResetPasswordFormData>({
    initialValues: {
      password: "",
    },
    validate: {
      password: (value) => {
        if (!value) return "Password is required";
        if (value.length < 8)
          return "Password must be at least 8 characters long";
        return null;
      },
    },
  });

  const handleSubmit = async (values: ResetPasswordFormData) => {
    if (!requestId) {
      setServerErrors({
        general: "Invalid reset link. Please request a new password reset.",
      });
      return;
    }

    setLoading(true);
    setServerErrors({});
    setSuccessMessage("");

    try {
      const success = await resetPassword({
        requestId,
        password: values.password,
        repeatPassword: values.password, // Send same value for API requirement
      });

      if (success) {
        setSuccessMessage(
          "Password reset successful! Redirecting to sign in..."
        );

        // Notify parent component and redirect after showing success message
        setTimeout(() => {
          onResetSuccess?.();
        }, 2000);
      } else {
        setServerErrors({
          general:
            "Failed to reset password. The link may have expired. Please request a new password reset.",
        });
      }
    } catch (error) {
      console.error("Password reset error:", error);
      setServerErrors({
        general:
          error instanceof Error
            ? error.message
            : "An error occurred while resetting password. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Show success state
  if (successMessage) {
    return (
      <Alert color="green" icon={<IconCheck size={16} />}>
        {successMessage}
      </Alert>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {serverErrors.general && (
          <Alert color="red" icon={<IconInfoCircle size={16} />}>
            {serverErrors.general}
          </Alert>
        )}

        <Text size="sm" c="dimmed" mb="xs">
          Enter your new password below. Make sure it's at least 8 characters
          long.
        </Text>

        <PasswordInput
          label="New Password"
          placeholder="Enter your new password"
          required
          {...form.getInputProps("password")}
          error={form.errors.password || serverErrors.password}
        />

        <Button type="submit" fullWidth size="md" mt="md" loading={loading}>
          Reset Password
        </Button>
      </Stack>
    </form>
  );
};
